import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { clearAccessToken, getAccessToken, setAccessToken as setTokenService } from '../services/authTokenService';
import apiClient from '../utils/apiClient';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [token, updateToken] = useState(() => getAccessToken());
    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);
    const refreshPromiseRef = useRef(null);
    const initialRefreshDoneRef = useRef(false);

    const setToken = useCallback((nextToken) => {
        updateToken(nextToken || '');
        setTokenService(nextToken || '');
    }, []);

    const logout = useCallback(() => {
        setToken('');
        clearAccessToken();
        apiClient.post('/auth/logout').catch(() => {
        });
    }, [setToken]);

    const tryRefreshToken = useCallback(async () => {
        if (refreshPromiseRef.current) return refreshPromiseRef.current;
        refreshPromiseRef.current = (async () => {
            try {
                const res = await apiClient.post('/auth/refresh');
                const result = res.data?.data || {};
                const newToken = result.accessToken;
                if (newToken) {
                    setToken(newToken);
                    return true;
                }
                return false;
            } catch {
                return false;
            } finally {
                refreshPromiseRef.current = null;
            }
        })();
        return refreshPromiseRef.current;
    }, [setToken]);

    useEffect(() => {
        if (token) {
            setTokenService(token);
        } else {
            clearAccessToken();
        }
    }, [token]);

    useEffect(() => {
        if (!initialRefreshDoneRef.current) {
            initialRefreshDoneRef.current = true;
            if (!token) {
                tryRefreshToken().finally(() => setAuthLoading(false));
            } else {
                setAuthLoading(false);
            }
        }
    }, [token, tryRefreshToken]);

    useEffect(() => {
        const interceptor = apiClient.interceptors.response.use(response => {
            return response;
        }, async error => {
            const originalRequest = error.config || {};
            const requestUrl = originalRequest.url || '';
            // Never retry the refresh endpoint itself — expired refresh
            // token would otherwise loop: refresh -> 401 -> refresh ...
            if (requestUrl.includes('/auth/refresh')) {
                return Promise.reject(error);
            }
            if (error.response && error.response.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;
                const refreshed = await tryRefreshToken();
                if (refreshed) {
                    originalRequest.headers = originalRequest.headers || {};
                    originalRequest.headers.Authorization = `Bearer ${getAccessToken()}`;
                    return apiClient(originalRequest);
                }
                const url = originalRequest.url || '';
                const isAuthEndpoint = url.includes('/auth/');
                clearAccessToken();
                document.cookie = 'user_lat=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure; SameSite=Lax';
                document.cookie = 'user_lng=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure; SameSite=Lax';
                updateToken('');
                if (typeof window !== 'undefined' && !isAuthEndpoint) {
                    window.dispatchEvent(new CustomEvent('ftr:auth-expired', {
                        detail: { returnPath: window.location.pathname + window.location.search },
                    }));
                }
            }
            return Promise.reject(error);
        });

        return () => {
            apiClient.interceptors.response.eject(interceptor);
        };
    }, [tryRefreshToken]);

    useEffect(() => {
        if (token) {
            try {
                const decoded = JSON.parse(atob(token.split('.')[1]));
                setUser(decoded);
            } catch {
                setUser(null);
            }
        } else {
            setUser(null);
        }
    }, [token]);

    const value = useMemo(() => ({
        token,
        user,
        authLoading,
        setToken,
        logout,
        tryRefreshToken,
    }), [token, user, authLoading, setToken, logout, tryRefreshToken]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuthContext() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
    return ctx;
}
